import { Button, Empty, Modal, PaginationProps, Spin } from 'antd';
import { getSettlementsMerchantApi } from 'apis/settlementAPI';
import { getTransactions } from 'apis/transactionAPI';
import { Box, Space } from 'components';
import {
  AddOwnerModal,
  MerchantAcquirerForm,
  MerchantBankForm,
  MerchantDetailsForm,
  MerchantDocuments,
  MerchantEcomForm,
  MerchantNoteForm,
  MerchantOwnersDetails,
  MerchantRevenueForm,
  MerchantStatusDetails,
  MerchantTerminalDetails,
  MerchantType,
  SandboxModal,
} from 'features/merchants/components';
import { MerchantConfiguration } from 'features/merchants/components/MerchantConfiguration';
import { MerchantContactForm } from 'features/merchants/components/MerchantContactForm';
import { MerchantUserDetails } from 'features/merchants/components/MerchantUserDetails';
import {
  useDocumentsApi,
  useFilesApi,
  useMerchant,
} from 'features/merchants/hooks';
import { merchantSelector } from 'features/merchants/redux';
import { BaseLayout } from 'features/navigation/components';
import { ProxyUserSettings } from 'features/settings';
import { SettlementsTable } from 'features/settlements/SettlementsTable/SettlementsTable';
import { TransactionsTable } from 'features/transactions';
import { userSelector } from 'features/user/redux/selectors';
import { FirebaseCollectionsType, getUserDatas } from 'firebaseAPI';
import { orderBy } from 'lodash';
import moment from 'moment';
import React, { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import { useFirestoreConnect } from 'react-redux-firebase';
import { useParams } from 'react-router';
import { styled } from 'theme';
import type { Firebase } from 'types/firebase';
import { SoftPos } from 'types/softpos';
import * as Styles from './MerchantPage.styles';
import { MerchantDeviceDetails } from 'features/merchants/components/MerchantDeviceDetails/MerchantDeviceDetails';

const StyledEmpty = styled(Empty)`
  && {
    margin: 50;
  }
`;

const MerchantPage: React.FC = () => {
  const [sandboxModal, setSandboxModal] = useState<boolean>(false);
  const [trnxItems, setTrnxItems] = useState<SoftPos.TrxnRaw[]>([]);
  const [stlmItems, setStlmItems] = useState<SoftPos.Settlement[]>([]);
  const [users, setUsers] = useState<Firebase.UserData[]>([]);
  const [lastTransactionDate] = useState<string | null>();
  const [transactionsError, setTransactionError] = useState<string | null>();
  const [settlementError, setSettlementError] = useState<string | null>();
  const { merchantId } = useParams<{ merchantId: string }>();
  const { updateMerchant, updateTerminalIMEI } = useMerchant(merchantId);
  const SOFTPOS_START_DATE = '2021-06-01T00:00:00.000Z';
  const user = useSelector(userSelector);
  const {
    handlers: { onDeleteAllImages },
  } = useFilesApi({});

  const fetchUsers = React.useCallback(async () => {
    setUsers(await getUserDatas(merchantId));
  }, [merchantId]);

  const fetchTransactions = React.useCallback(async () => {
    try {
      const resp = await getTransactions(
        moment().subtract(2, 'weeks'),
        moment(),
        merchantId,
      );
      const trxnItems = orderBy(resp, ['utcTime'], ['desc']);
      setTrnxItems(trxnItems);
    } catch (exception) {
      setTransactionError(exception?.message);
    }
  }, [merchantId]);

  const fetchSettlements = React.useCallback(async () => {
    try {
      const settlementResults = await getSettlementsMerchantApi(
        merchantId,
        0,
        10,
      );
      const stlmItems = orderBy(settlementResults, ['startedAt'], ['desc']);
      setStlmItems(stlmItems);
    } catch (exception) {
      setSettlementError(exception?.message);
    }
  }, [merchantId]);

  React.useEffect(() => {
    fetchTransactions();
    fetchSettlements();
    fetchUsers();
  }, [merchantId]);

  const merchant: Firebase.Merchant = useSelector(merchantSelector(merchantId));
  const {
    handlers: { onDeleteAllDocuments },
  } = useDocumentsApi({
    userId: merchant?.onboardingId,
    merchantId: merchant?.merchantId,
    accoutId: merchant?.accountId,
  });
  useFirestoreConnect({
    collection: FirebaseCollectionsType.MERCHANTS,
    storeAs: `merchant_${merchantId}`,
    doc: merchantId,
  });

  const handleUpdatingTerminalIMEI = useCallback(
    (terminalId: string, value: string) => {
      return updateTerminalIMEI(merchantId, terminalId, { imei: value });
    },
    [merchantId, updateTerminalIMEI],
  );

  const handleNoTransactions = useCallback(() => {
    if (!lastTransactionDate) {
      return (
        <StyledEmpty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="No transactions"
        />
      );
    }
    return (
      <div>
        <Space size="xl" />
        Last transaction happened at: {lastTransactionDate}
      </div>
    );
  }, [lastTransactionDate]);

  const handleUpdateProperty = useCallback(
    (name: string, value: any) => {
      return updateMerchant({ [name]: value });
    },
    [updateMerchant],
  );

  const handleChangeReadyForApplication = useCallback(
    (readyForApplication: number | null) => {
      return updateMerchant({ readyForApplication });
    },
    [updateMerchant],
  );

  const handleChangeOwnerData = useCallback(
    (ownerIndex: number, property: string, value: string | number) => {
      const ownerData = merchant?.ownerData?.map((owner, index) => {
        if (index === ownerIndex) {
          return {
            ...owner,
            [property]: value,
          };
        }

        return owner;
      });

      return handleUpdateProperty('ownerData', ownerData);
    },
    [handleUpdateProperty, merchant?.ownerData],
  );
  /**
   * _onHandleConfirm
   * confirm clearhausEcomMerchantId
   */
  const _onHandleEcomIdConfirm = async (newClearhausEcomMerchantId: string) => {
    await handleUpdateProperty(
      'clearhausEcomMerchantId',
      newClearhausEcomMerchantId,
    );
    // Cleanup socialSecurityNumber for FO merchants (GDPR)
    const ownerData = merchant?.ownerData.map((item) => ({
      ...item, // Spread all existing properties of the item
      socialSecurityNumber: null, // Set only `someKey` to null
    }));
    await handleUpdateProperty('ownerData', ownerData);
    if (confirm('Do you want to delete KYC images and documents?') == true) {
      await onDeleteAllImages(merchant.onboardingId, merchant.merchantId);
      await onDeleteAllDocuments(merchant.onboardingId);
    } else {
    }
  };
  /**
   * _onHandleConfirm
   * confirm clearhaus id
   */
  const _onHandleConfirm = async (
    inputName: string,
    newClearhausMerchantId: string,
  ) => {
    await handleUpdateProperty(inputName, newClearhausMerchantId);
    await handleUpdateProperty('clearhausKYCdone', Date.now());
    // Cleanup socialSecurityNumber for FO merchants (GDPR)
    const ownerData = merchant?.ownerData.map((item) => ({
      ...item,
      socialSecurityNumber: null,
    }));
    await handleUpdateProperty('ownerData', ownerData);
    if (confirm('Do you want to delete KYC images and documents?') == true) {
      await onDeleteAllImages(merchant.onboardingId, merchant.merchantId);
      await onDeleteAllDocuments(merchant.onboardingId);
    } else {
    }
  };

  const handleChangeAcquirerFields = useCallback(
    (inputName: string, value: string | number, forced = false) => {
      if (inputName === 'mcc') {
        handleUpdateProperty(inputName, value);
      } else if (inputName === 'merchantContract') {
        handleUpdateProperty(inputName, value);
      } else {
        Modal.confirm({
          title: 'Confirm',
          content: 'Are you sure the merchant contract is correct?',
          okText: 'Confirm',
          cancelText: 'Cancel',
          onOk: async () => {
            const newClearhausMerchantId = value.toString().trim();
            await _onHandleConfirm(inputName, newClearhausMerchantId);
          },
        });
      }
    },
    [handleUpdateProperty],
  );

  if (!merchant) {
    return (
      <BaseLayout fullHeight>
        <Spin />
      </BaseLayout>
    );
  }

  return (
    <BaseLayout>
      <SandboxModal
        merchant={merchant}
        showSandboxModal={sandboxModal}
        hideSandboxModal={() => setSandboxModal(false)}
      />
      <AddOwnerModal merchant={merchant} />

      <Styles.Container>
        <MerchantStatusDetails
          merchant={merchant}
          onChangeReadyForApplication={handleChangeReadyForApplication}
        />
        <Styles.GridContainer>
          <div>
            <Box display="flex" flexDirection="column">
              {user.userRoles.includes('push-to-pay') &&
                !merchant.enabledFeatures?.includes('push-to-pay') && (
                  <Button onClick={() => setSandboxModal(true)}>
                    Enable and add sandbox user
                  </Button>
                )}
              <ProxyUserSettings merchant={merchant} />
            </Box>
            <MerchantOwnersDetails
              merchantId={merchant.merchantId}
              onboardingId={merchant.onboardingId}
              owners={merchant.ownerData}
              onSave={handleChangeOwnerData}
            />
            <Space size="xl" />
            <MerchantDocuments merchant={merchant} />
            <Space size="xl" />
            <MerchantTerminalDetails
              merchantId={merchantId}
              accountId={merchant.accountId}
              // Hide old terminals (non-softpay)
              terminals={merchant.terminalCollection?.filter((terminal) =>
                terminal?.terminalId?.includes('ti_'),
              )}
              onSave={handleUpdatingTerminalIMEI}
            />
            <Space size="xl" />
            <MerchantUserDetails merchantId={merchantId} users={users} />
            <Space size="xl" />
            <MerchantDeviceDetails
              merchantId={merchantId}
              accountId={merchant.accountId}
            />
            <Space size="xl" />
            {merchant.contact && (
              <MerchantContactForm
                contact={merchant.contact}
                onSave={handleUpdateProperty}
              />
            )}
            <Space size="xl" />
            <MerchantConfiguration
              merchant={merchant}
              onSave={handleUpdateProperty}
            />
          </div>
          <div>
            <MerchantType
              merchant={merchant}
              onSave={handleUpdateProperty}
              onUpdateEcomId={_onHandleEcomIdConfirm}
            />
            <Space size="xl" />
            <MerchantNoteForm
              merchant={merchant}
              onSave={handleUpdateProperty}
            />
            <MerchantEcomForm
              merchant={merchant}
              onSave={handleUpdateProperty}
            />
            <Space size="xl" />
            <MerchantDetailsForm
              merchant={merchant}
              onSave={handleUpdateProperty}
            />

            <MerchantBankForm
              merchant={merchant}
              onSave={handleUpdateProperty}
            />

            <MerchantAcquirerForm
              merchant={merchant}
              onSave={handleChangeAcquirerFields}
            />

            <MerchantRevenueForm
              merchant={merchant}
              onSave={handleUpdateProperty}
            />
          </div>
        </Styles.GridContainer>
      </Styles.Container>
      {transactionsError ? (
        <div>{transactionsError}</div>
      ) : trnxItems.length > 0 ? (
        <TransactionsTable
          trxnItems={trnxItems}
          paginate={undefined}
          merchantsFilterOpt={[]}
          isFetching={false}
          tableChangeHandler={(pagination: PaginationProps) => {}}
        />
      ) : (
        handleNoTransactions()
      )}
      {settlementError ? (
        <div>{settlementError}</div>
      ) : stlmItems.length > 0 ? (
        <SettlementsTable stlmItems={stlmItems} />
      ) : (
        <StyledEmpty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="No settlements"
        />
      )}
    </BaseLayout>
  );
};

export { MerchantPage };
